/**
 * LuxireStorage — Shopify App Proxy client for customer profiles & wishlists.
 *
 * Logged-in customers: data stored in Shopify metafields via App Proxy.
 * Guests: data stored in localStorage only (existing behavior).
 * Reads use localStorage as a fast cache, hydrated from Liquid on page load.
 *
 * Usage:
 *   const profiles = await LuxireStorage.getMeasurementProfiles();
 *   await LuxireStorage.saveMeasurementProfile({ name: 'My Pants', ... });
 *   await LuxireStorage.addToWishlist({ handle: 'grey-pants', ... });
 */

const LuxireStorage = (() => {

  const PROXY_BASE = '/apps/luxire-api';
  const CACHE_KEY_MEASUREMENTS = 'luxire_measurement_profiles';
  const CACHE_KEY_STYLES = 'luxire_style_profiles';
  const CACHE_KEY_WISHLIST = 'luxire_wishlist';
  const SYNC_FLAG = 'luxire_synced';

  // True if customer is logged in (set by Liquid in the page)
  function isLoggedIn() {
    return !!window.__luxireCustomerId;
  }

  // -------------------------------------------------------------------------
  // API helper
  // -------------------------------------------------------------------------

  async function api(method, path, body = null) {
    const options = {
      method,
      headers: { 'Content-Type': 'application/json' },
    };
    if (body) options.body = JSON.stringify(body);

    const response = await fetch(`${PROXY_BASE}${path}`, options);
    const data = await response.json();

    if (!response.ok) {
      console.error(`LuxireStorage API error: ${method} ${path}`, data);
      throw new Error(data.error || 'api_error');
    }

    return data;
  }

  // -------------------------------------------------------------------------
  // localStorage cache helpers
  // -------------------------------------------------------------------------

  function cacheRead(key) {
    try {
      const raw = localStorage.getItem(key);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }

  function cacheWrite(key, value) {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (e) {
      // localStorage full or blocked — silent fail, server is source of truth
    }
  }

  // -------------------------------------------------------------------------
  // Hydrate cache from Liquid-embedded data (called on page load)
  // -------------------------------------------------------------------------

  function hydrateFromLiquid() {
    // If the page has embedded profile data from Liquid (e.g. on account page),
    // use it to pre-populate the cache for instant reads.
    var el = document.querySelector('[data-luxire-profiles]');
    if (!el) return;

    try {
      var measurements = el.getAttribute('data-measurement-profiles');
      if (measurements) {
        var mData = JSON.parse(measurements);
        // Unwrap metafield wrapper { version, profiles } if present
        if (mData && mData.profiles && Array.isArray(mData.profiles)) mData = mData.profiles;
        if (Array.isArray(mData)) cacheWrite(CACHE_KEY_MEASUREMENTS, mData);
      }

      var styles = el.getAttribute('data-style-profiles');
      if (styles) {
        var sData = JSON.parse(styles);
        if (sData && sData.profiles && Array.isArray(sData.profiles)) sData = sData.profiles;
        if (Array.isArray(sData)) cacheWrite(CACHE_KEY_STYLES, sData);
      }

      var wishlist = el.getAttribute('data-wishlist');
      if (wishlist) {
        var wData = JSON.parse(wishlist);
        if (wData && wData.items && Array.isArray(wData.items)) wData = wData.items;
        if (Array.isArray(wData)) cacheWrite(CACHE_KEY_WISHLIST, wData);
      }
    } catch (e) {
      console.warn('LuxireStorage: failed to hydrate from Liquid', e);
    }
  }

  // -------------------------------------------------------------------------
  // Sync — one-time migration from localStorage to server
  // -------------------------------------------------------------------------

  async function syncIfNeeded() {
    if (!isLoggedIn()) return;
    if (localStorage.getItem(SYNC_FLAG) === '1') return;

    var payload = {};

    var oldMeasurements = cacheRead(CACHE_KEY_MEASUREMENTS);
    var oldStyles = cacheRead(CACHE_KEY_STYLES);
    var oldWishlist = cacheRead(CACHE_KEY_WISHLIST);
    var oldLegacy = cacheRead('luxire_measurement_profile');

    // Also check old account hub format
    var hub = cacheRead('luxire_account_hub');
    if (hub) {
      if (!oldMeasurements && hub.measurementProfiles) oldMeasurements = hub.measurementProfiles;
      if (!oldStyles && hub.styleProfiles) oldStyles = hub.styleProfiles;
      if (!oldWishlist && hub.wishlist) oldWishlist = hub.wishlist;
    }

    if (oldMeasurements && oldMeasurements.length) {
      payload.measurementProfiles = oldMeasurements;
    }
    if (oldStyles && oldStyles.length) {
      payload.styleProfiles = oldStyles;
    }
    if (oldWishlist) {
      payload.wishlist = Array.isArray(oldWishlist) ? oldWishlist : (oldWishlist.items || []);
    }
    if (oldLegacy) {
      payload.legacyMeasurementProfile = oldLegacy;
    }

    try {
      var result = await api('POST', '/sync', payload);

      // Replace localStorage with server's merged data
      cacheWrite(CACHE_KEY_MEASUREMENTS, result.data.measurementProfiles);
      cacheWrite(CACHE_KEY_STYLES, result.data.styleProfiles);

      // Normalize wishlist items from server before caching
      var syncedWishlist = result.data.wishlist || [];
      if (Array.isArray(syncedWishlist)) {
        syncedWishlist = syncedWishlist.map(normalizeWishlistItem);
      }
      cacheWrite(CACHE_KEY_WISHLIST, syncedWishlist);

      // Mark as synced
      localStorage.setItem(SYNC_FLAG, '1');

      console.log('LuxireStorage: sync complete', result.stats);
    } catch (err) {
      console.error('LuxireStorage: sync failed, will retry next page load', err);
      // Don't set SYNC_FLAG — will retry next time
    }
  }

  // -------------------------------------------------------------------------
  // Unwrap helper — server may return profiles inside metafield wrapper
  // { version: 1, profiles: [...] } or directly as an array [...]
  // -------------------------------------------------------------------------
  function unwrapProfiles(data) {
    if (Array.isArray(data)) return data;
    if (data && data.profiles && Array.isArray(data.profiles)) return data.profiles;
    if (data && typeof data === 'object' && !Array.isArray(data)) return [];
    return [];
  }

  // -------------------------------------------------------------------------
  // MEASUREMENT PROFILES
  // -------------------------------------------------------------------------

  async function getMeasurementProfiles() {
    if (isLoggedIn()) {
      var cached = cacheRead(CACHE_KEY_MEASUREMENTS);
      if (cached) return unwrapProfiles(cached);

      try {
        var result = await api('GET', '/profiles/measurements');
        var profiles = unwrapProfiles(result.profiles);
        cacheWrite(CACHE_KEY_MEASUREMENTS, profiles);
        return profiles;
      } catch (e) {
        return cached ? unwrapProfiles(cached) : [];
      }
    }

    // Guest: localStorage only (old behavior)
    var hub = cacheRead('luxire_account_hub');
    return (hub && hub.measurementProfiles) ? hub.measurementProfiles : [];
  }

  async function saveMeasurementProfile(profile) {
    if (!isLoggedIn()) {
      var hub = cacheRead('luxire_account_hub') || {};
      hub.measurementProfiles = hub.measurementProfiles || [];
      hub.measurementProfiles.push(profile);
      cacheWrite('luxire_account_hub', hub);
      return profile;
    }

    var result = profile.id
      ? await api('PUT', '/profiles/measurements/' + profile.id, profile)
      : await api('POST', '/profiles/measurements', profile);

    // Refresh cache
    var all = await api('GET', '/profiles/measurements');
    cacheWrite(CACHE_KEY_MEASUREMENTS, unwrapProfiles(all.profiles));

    return result.profile;
  }

  async function deleteMeasurementProfile(id) {
    if (!isLoggedIn()) {
      var hub = cacheRead('luxire_account_hub') || {};
      hub.measurementProfiles = (hub.measurementProfiles || []).filter(function(p) { return p.id !== id; });
      cacheWrite('luxire_account_hub', hub);
      return;
    }

    await api('DELETE', '/profiles/measurements/' + id);

    // Refresh cache
    var all = await api('GET', '/profiles/measurements');
    cacheWrite(CACHE_KEY_MEASUREMENTS, unwrapProfiles(all.profiles));
  }

  // -------------------------------------------------------------------------
  // STYLE PROFILES
  // -------------------------------------------------------------------------

  async function getStyleProfiles() {
    if (isLoggedIn()) {
      var cached = cacheRead(CACHE_KEY_STYLES);
      if (cached) return unwrapProfiles(cached);

      try {
        var result = await api('GET', '/profiles/styles');
        var profiles = unwrapProfiles(result.profiles);
        cacheWrite(CACHE_KEY_STYLES, profiles);
        return profiles;
      } catch (e) {
        return cached ? unwrapProfiles(cached) : [];
      }
    }

    var hub = cacheRead('luxire_account_hub');
    return (hub && hub.styleProfiles) ? hub.styleProfiles : [];
  }

  async function saveStyleProfile(profile) {
    if (!isLoggedIn()) {
      var hub = cacheRead('luxire_account_hub') || {};
      hub.styleProfiles = hub.styleProfiles || [];
      hub.styleProfiles.push(profile);
      cacheWrite('luxire_account_hub', hub);
      return profile;
    }

    var result = profile.id
      ? await api('PUT', '/profiles/styles/' + profile.id, profile)
      : await api('POST', '/profiles/styles', profile);

    var all = await api('GET', '/profiles/styles');
    cacheWrite(CACHE_KEY_STYLES, unwrapProfiles(all.profiles));

    return result.profile;
  }

  async function deleteStyleProfile(id) {
    if (!isLoggedIn()) {
      var hub = cacheRead('luxire_account_hub') || {};
      hub.styleProfiles = (hub.styleProfiles || []).filter(function(p) { return p.id !== id; });
      cacheWrite('luxire_account_hub', hub);
      return;
    }

    await api('DELETE', '/profiles/styles/' + id);

    var all = await api('GET', '/profiles/styles');
    cacheWrite(CACHE_KEY_STYLES, unwrapProfiles(all.profiles));
  }

  // -------------------------------------------------------------------------
  // WISHLIST
  // -------------------------------------------------------------------------

  // Extract a product handle from whatever fields are available
  function extractHandle(item) {
    if (item.handle) return item.handle;
    if (item.product_handle) return item.product_handle;
    if (item.productHandle) return item.productHandle;
    // Try to extract from URL: "/products/some-handle" or "/products/some-handle?..."
    var url = item.url || item.product_url || item.productUrl || '';
    var match = url.match(/\/products\/([^?#\/]+)/);
    if (match) return match[1];
    return null;
  }

  // Normalize a server item into our expected format.
  // Server stores wishlist as plain handle strings: ["grey-cotton-dress-pant", ...]
  function normalizeWishlistItem(item) {
    // Item is a plain string (handle) — most common server format
    if (typeof item === 'string') {
      return {
        handle: item,
        title: '',
        image: '',
        price: '',
        url: '/products/' + item,
        variantId: null,
        added: Date.now()
      };
    }

    // Item is already an object (from localStorage or rich API)
    var handle = extractHandle(item);
    return {
      handle: handle,
      title: item.title || item.product_title || item.productTitle || '',
      image: item.image || item.featured_image || item.product_image || item.productImage || '',
      price: item.price || item.product_price || '',
      url: item.url || item.product_url || (handle ? '/products/' + handle : ''),
      variantId: item.variantId || item.variant_id || item.variantID || null,
      added: item.added || item.created_at || item.createdAt || Date.now()
    };
  }

  async function getWishlist() {
    if (isLoggedIn()) {
      var cached = cacheRead(CACHE_KEY_WISHLIST);
      if (cached) return cached;

      try {
        var result = await api('GET', '/wishlist');
        var items = (result.items || result.wishlist || result.data || result || []);
        if (!Array.isArray(items)) items = [];
        items = items.map(normalizeWishlistItem);
        cacheWrite(CACHE_KEY_WISHLIST, items);
        return items;
      } catch (e) {
        return cached || [];
      }
    }

    var hub = cacheRead('luxire_account_hub');
    return (hub && hub.wishlist) ? hub.wishlist : [];
  }

  // Force-refresh wishlist from server (bypasses cache). Use on page load / tab switch.
  async function refreshWishlist() {
    if (!isLoggedIn()) {
      // For guests, cache is the only source
      return getWishlistSync();
    }

    try {
      var result = await api('GET', '/wishlist');
      console.log('LuxireStorage: server wishlist response', result);

      var items = (result.items || result.wishlist || result.data || result || []);
      if (!Array.isArray(items)) items = [];
      items = items.map(normalizeWishlistItem);
      cacheWrite(CACHE_KEY_WISHLIST, items);
      return items;
    } catch (e) {
      console.warn('LuxireStorage: failed to refresh wishlist from server', e);
      return getWishlistSync();
    }
  }

  async function addToWishlist(item) {
    if (!isLoggedIn()) {
      var hub = cacheRead('luxire_account_hub') || {};
      hub.wishlist = hub.wishlist || [];
      hub.wishlist.push(item);
      cacheWrite('luxire_account_hub', hub);
      return item;
    }

    var result = await api('POST', '/wishlist', item);

    var all = await api('GET', '/wishlist');
    var items = (all.items || all.wishlist || all.data || all || []);
    if (!Array.isArray(items)) items = [];
    items = items.map(normalizeWishlistItem);
    cacheWrite(CACHE_KEY_WISHLIST, items);

    return result.item;
  }

  async function removeFromWishlist(handle) {
    if (!isLoggedIn()) {
      var hub = cacheRead('luxire_account_hub') || {};
      hub.wishlist = (hub.wishlist || []).filter(function(w) { return w.handle !== handle; });
      cacheWrite('luxire_account_hub', hub);
      return;
    }

    await api('DELETE', '/wishlist/' + encodeURIComponent(handle));

    var all = await api('GET', '/wishlist');
    var items = (all.items || all.wishlist || all.data || all || []);
    if (!Array.isArray(items)) items = [];
    items = items.map(normalizeWishlistItem);
    cacheWrite(CACHE_KEY_WISHLIST, items);
  }

  function isWishlisted(handle) {
    var items;
    if (isLoggedIn()) {
      items = cacheRead(CACHE_KEY_WISHLIST) || [];
    } else {
      var hub = cacheRead('luxire_account_hub');
      items = (hub && hub.wishlist) ? hub.wishlist : [];
    }
    return items.some(function(w) { return w.handle === handle; });
  }

  // -------------------------------------------------------------------------
  // INIT — call on every page load
  // -------------------------------------------------------------------------

  async function init() {
    hydrateFromLiquid();

    if (isLoggedIn()) {
      await syncIfNeeded();
    }
  }

  // -------------------------------------------------------------------------
  // SYNC READS — fast cache-only reads for UI rendering
  // These return whatever is in localStorage right now (no server fetch).
  // -------------------------------------------------------------------------

  function getWishlistSync() {
    var cached = cacheRead(CACHE_KEY_WISHLIST);
    if (cached && Array.isArray(cached)) {
      // Normalize items that are still raw strings from server
      var needsNormalize = cached.some(function(item) {
        return typeof item === 'string' || (item && !item.handle);
      });
      if (needsNormalize) {
        cached = cached.map(normalizeWishlistItem);
        cacheWrite(CACHE_KEY_WISHLIST, cached);
      }
      return cached;
    }
    // Fallback: old account-hub format
    var hub = cacheRead('luxire_account_hub');
    return (hub && hub.wishlist) ? hub.wishlist : [];
  }

  function getMeasurementProfilesSync() {
    var cached = cacheRead(CACHE_KEY_MEASUREMENTS);
    // Unwrap metafield wrapper if present
    if (cached && cached.profiles && Array.isArray(cached.profiles)) cached = cached.profiles;
    if (cached && Array.isArray(cached)) return cached;
    var hub = cacheRead('luxire_account_hub');
    return (hub && hub.measurementProfiles) ? hub.measurementProfiles : (hub && hub.measProfiles) ? hub.measProfiles : [];
  }

  function getStyleProfilesSync() {
    var cached = cacheRead(CACHE_KEY_STYLES);
    if (cached && cached.profiles && Array.isArray(cached.profiles)) cached = cached.profiles;
    if (cached && Array.isArray(cached)) return cached;
    var hub = cacheRead('luxire_account_hub');
    return (hub && hub.styleProfiles) ? hub.styleProfiles : [];
  }

  // Account-hub store: generic key-value for non-profile hub data
  // (gift cards, referral codes, coupons, etc.)
  function getAccountHub() {
    return cacheRead('luxire_account_hub') || {};
  }
  function setAccountHub(data) {
    cacheWrite('luxire_account_hub', data);
  }

  // Recently viewed products (local-only, no server sync)
  function getRecentlyViewed() {
    return cacheRead('luxire_recently_viewed') || [];
  }
  function saveRecentlyViewed(items) {
    cacheWrite('luxire_recently_viewed', items);
  }

  // Collection view preference (local-only)
  function getViewPreference() {
    try { return localStorage.getItem('luxire_view'); } catch(e) { return null; }
  }
  function saveViewPreference(view) {
    try { localStorage.setItem('luxire_view', view); } catch(e) {}
  }

  // Save wishlist array directly (for bulk updates like splice/filter)
  function saveWishlistSync(items) {
    cacheWrite(CACHE_KEY_WISHLIST, items);
    // Also update hub format for backward compat
    var hub = cacheRead('luxire_account_hub') || {};
    hub.wishlist = items;
    cacheWrite('luxire_account_hub', hub);
  }

  // Enrich wishlist items that are missing product data (title, image, url).
  // This happens when server stores only handles. Fetches from Shopify product JSON.
  async function enrichWishlistItems(items) {
    if (!items || !items.length) return items;

    var needsEnrich = items.filter(function(item) {
      return item.handle && (!item.title || !item.image || !item.url);
    });

    console.log('LuxireStorage: enrichment check —', needsEnrich.length, 'of', items.length, 'items need enrichment');
    if (needsEnrich.length === 0) return items;

    var enriched = false;
    await Promise.all(needsEnrich.map(function(item) {
      var url = '/products/' + encodeURIComponent(item.handle) + '.js';
      console.log('LuxireStorage: fetching product data from', url);
      return fetch(url)
        .then(function(r) {
          console.log('LuxireStorage: product fetch', item.handle, '→', r.status);
          return r.ok ? r.json() : null;
        })
        .then(function(product) {
          if (!product) return;
          if (!item.title) item.title = product.title;
          if (!item.image) item.image = product.featured_image || (product.images && product.images[0]) || '';
          if (!item.url) item.url = '/products/' + item.handle;
          if (!item.price) {
            var p = product.price || (product.variants && product.variants[0] && product.variants[0].price);
            if (p) item.price = '$' + (parseFloat(p) / 100).toFixed(2);
          }
          if (!item.variantId && product.variants && product.variants[0]) {
            item.variantId = product.variants[0].id;
          }
          enriched = true;
          console.log('LuxireStorage: enriched', item.handle, '→', item.title, item.image ? '(has image)' : '(no image)');
        })
        .catch(function(err) {
          console.warn('LuxireStorage: enrichment failed for', item.handle, err);
        });
    }));

    if (enriched) {
      saveWishlistSync(items);
      console.log('LuxireStorage: enriched data saved to cache');
    }

    return items;
  }

  // -------------------------------------------------------------------------
  // FORCE-FETCH — always goes to server, bypasses cache. Used when Profiles
  // tab opens to ensure migrated metafield data is visible.
  // -------------------------------------------------------------------------

  async function fetchMeasurementProfiles() {
    if (!isLoggedIn()) return getMeasurementProfilesSync();
    try {
      var result = await api('GET', '/profiles/measurements');
      var profiles = unwrapProfiles(result.profiles);
      cacheWrite(CACHE_KEY_MEASUREMENTS, profiles);
      return profiles;
    } catch (e) {
      console.warn('LuxireStorage: server fetch failed for measurements, using cache', e);
      return getMeasurementProfilesSync();
    }
  }

  async function fetchStyleProfiles() {
    if (!isLoggedIn()) return getStyleProfilesSync();
    try {
      var result = await api('GET', '/profiles/styles');
      var profiles = unwrapProfiles(result.profiles);
      cacheWrite(CACHE_KEY_STYLES, profiles);
      return profiles;
    } catch (e) {
      console.warn('LuxireStorage: server fetch failed for styles, using cache', e);
      return getStyleProfilesSync();
    }
  }

  // Save measurement profiles array directly
  function saveMeasurementProfilesSync(profiles) {
    cacheWrite(CACHE_KEY_MEASUREMENTS, profiles);
    var hub = cacheRead('luxire_account_hub') || {};
    hub.measProfiles = profiles;
    hub.measurementProfiles = profiles;
    cacheWrite('luxire_account_hub', hub);
  }

  // Save style profiles array directly
  function saveStyleProfilesSync(profiles) {
    cacheWrite(CACHE_KEY_STYLES, profiles);
    var hub = cacheRead('luxire_account_hub') || {};
    hub.styleProfiles = profiles;
    cacheWrite('luxire_account_hub', hub);
  }

  // -------------------------------------------------------------------------
  // PUBLIC API
  // -------------------------------------------------------------------------

  return {
    init: init,

    // Measurement profiles (async — server-synced for logged-in users)
    getMeasurementProfiles: getMeasurementProfiles,
    saveMeasurementProfile: saveMeasurementProfile,
    deleteMeasurementProfile: deleteMeasurementProfile,

    // Style profiles (async)
    getStyleProfiles: getStyleProfiles,
    saveStyleProfile: saveStyleProfile,
    deleteStyleProfile: deleteStyleProfile,

    // Wishlist (async)
    getWishlist: getWishlist,
    refreshWishlist: refreshWishlist,
    addToWishlist: addToWishlist,
    removeFromWishlist: removeFromWishlist,
    isWishlisted: isWishlisted,

    // Sync reads (cache-only, for fast UI rendering)
    getWishlistSync: getWishlistSync,
    getMeasurementProfilesSync: getMeasurementProfilesSync,
    getStyleProfilesSync: getStyleProfilesSync,
    saveWishlistSync: saveWishlistSync,
    enrichWishlistItems: enrichWishlistItems,
    saveMeasurementProfilesSync: saveMeasurementProfilesSync,
    saveStyleProfilesSync: saveStyleProfilesSync,

    // Force-fetch from server (bypass cache — for profile tab refresh)
    fetchMeasurementProfiles: fetchMeasurementProfiles,
    fetchStyleProfiles: fetchStyleProfiles,

    // Account hub store (non-profile data: gift cards, coupons, referral, etc.)
    getAccountHub: getAccountHub,
    setAccountHub: setAccountHub,

    // Recently viewed products (local-only)
    getRecentlyViewed: getRecentlyViewed,
    saveRecentlyViewed: saveRecentlyViewed,

    // Collection view preference (local-only)
    getViewPreference: getViewPreference,
    saveViewPreference: saveViewPreference,

    // Raw API access (for support hub and other modules)
    api: api,
  };

})();